/**
 * Store listings waiting for a person (ADR-035 §12) — the review queue's
 * second section. The image check was unsure of each icon; everything else
 * about the listing passed. Approve puts it live (or applies the change);
 * reject takes a reason the submitter sees, and a new listing's bought slot
 * is refunded with it.
 *
 * The icon is unchecked art: it is fetched privately (never from the CDN) and
 * shown as an image only.
 */
import { useEffect, useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ImageOff, Loader2, X } from 'lucide-react';
import api, { type AdminStoreListingReview } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { NotificationBadge } from '@/components/ui/notification-badge';
import { useToast } from '@/components/ui/use-toast';

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Something went wrong';
}

/** The held icon, fetched with the reviewer's session and shown from memory. */
function HeldIcon({ review }: { review: AdminStoreListingReview }): ReactElement {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let url: string | null = null;
    let cancelled = false;
    void api.adminGetStoreListingReviewIcon(review.reviewId).then((blob) => {
      if (cancelled) return;
      if (!blob) {
        setFailed(true);
        return;
      }
      url = URL.createObjectURL(blob);
      setSrc(url);
    });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [review.reviewId]);
  if (src) {
    return (
      <img
        src={src}
        alt={`Icon submitted for ${review.title}`}
        className="h-24 w-24 rounded-2xl border border-border object-cover"
      />
    );
  }
  return (
    <div className="flex h-24 w-24 items-center justify-center rounded-2xl border border-border bg-muted">
      {failed ? (
        <ImageOff className="h-6 w-6 text-muted-foreground" aria-label="Icon unavailable" />
      ) : (
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      )}
    </div>
  );
}

function ReviewCard({ review }: { review: AdminStoreListingReview }): ReactElement {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.submissions.storeListingReviews() });

  const approve = useMutation({
    mutationFn: () => api.adminApproveStoreListingReview(review.reviewId),
    onSuccess: () => toast({ title: `${review.title} is live` }),
    onError: (err) =>
      toast({ title: 'Not approved', description: errorMessage(err), variant: 'destructive' }),
    onSettled: refresh,
  });
  const reject = useMutation({
    mutationFn: () => api.adminRejectStoreListingReview(review.reviewId, reason.trim()),
    onSuccess: (result) =>
      toast({
        title: `${review.title} not approved`,
        description:
          result.refund.refundedCents > 0
            ? `Its slot was refunded ($${(result.refund.refundedCents / 100).toFixed(2)}).`
            : result.refund.waiverRestored
              ? 'Its free listing was given back.'
              : undefined,
      }),
    onError: (err) =>
      toast({ title: 'Not rejected', description: errorMessage(err), variant: 'destructive' }),
    onSettled: refresh,
  });
  const busy = approve.isPending || reject.isPending;

  return (
    <li className="flex flex-col gap-4 p-4 sm:flex-row">
      <HeldIcon review={review} />
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{review.title}</span>
          <Badge variant="outline">{review.kind === 'edit' ? 'Change' : 'New listing'}</Badge>
          {review.findings.reason && (
            <Badge variant="secondary">Flagged: {review.findings.reason}</Badge>
          )}
        </div>
        <p className="text-sm text-muted-foreground">{review.description}</p>
        <p className="text-xs text-muted-foreground">
          In {review.archeName} at <span className="font-mono">{review.path}</span> · submitted{' '}
          {new Date(review.submittedAt).toLocaleString()}
        </p>
        {rejecting ? (
          <form
            className="flex flex-col gap-2 pt-2 sm:flex-row"
            onSubmit={(e) => {
              e.preventDefault();
              reject.mutate();
            }}
          >
            <Input
              aria-label={`Why ${review.title} is not approved`}
              placeholder="Why (the submitter sees this)"
              maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="h-9 flex-1"
            />
            <Button type="submit" size="sm" variant="destructive" disabled={!reason.trim() || busy}>
              Reject
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setRejecting(false)}>
              Cancel
            </Button>
          </form>
        ) : (
          <div className="flex gap-2 pt-2">
            <Button size="sm" className="gap-1" disabled={busy} onClick={() => approve.mutate()}>
              <Check className="h-4 w-4" />
              Approve
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="gap-1"
              disabled={busy}
              onClick={() => setRejecting(true)}
            >
              <X className="h-4 w-4" />
              Reject…
            </Button>
          </div>
        )}
      </div>
    </li>
  );
}

export function StoreListingReviewQueue(): ReactElement {
  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.submissions.storeListingReviews(),
    queryFn: () => api.adminListStoreListingReviews(),
  });
  return (
    <section className="mt-10" aria-labelledby="store-listing-reviews">
      <div className="mb-4 flex items-center gap-3">
        <h2 id="store-listing-reviews" className="text-xl font-semibold">
          Store listings
        </h2>
        <NotificationBadge count={data?.pending ?? 0} aria-label="Store listings awaiting review" />
      </div>
      <p className="mb-4 text-sm text-muted-foreground">
        Extra Store listings whose icon the image check could not clear. Everything else passed.
      </p>
      {isLoading && <p className="text-muted-foreground">Loading…</p>}
      {error && <p className="text-destructive">Failed to load: {errorMessage(error)}</p>}
      {data && data.reviews.length === 0 && (
        <Card className="p-6 text-center">
          <p className="text-muted-foreground">No listing is waiting.</p>
        </Card>
      )}
      {data && data.reviews.length > 0 && (
        <Card className="overflow-hidden">
          <ul className="divide-y divide-border">
            {data.reviews.map((review) => (
              <ReviewCard key={review.reviewId} review={review} />
            ))}
          </ul>
        </Card>
      )}
    </section>
  );
}
